//START GENAI@GHCOPILOT
import { setCookie } from '@visa/lib-cookie';
import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { pushErrors, pushIdentityLookupRequest, pushIdentityLookupResponse } from 'common/gamp';
import { saveMeta, saveUser, UserProfile } from 'communicator/store';
import {
  getApiKey,
  getFeatureToggles,
  getIsFeatureToggleEnabled,
  getIsVdiDisabled,
  getIsVisaSRCi,
  getOrgId,
  getSessionId,
  getThmProfileHint
} from 'communicator/store/selectors';
import { fetchOrchestratorIdLookup } from 'communicator/utils/api/id-lookup';
import { getHeader } from 'communicator/utils/fetch';
import getGenericError from 'communicator/utils/generic-error';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import createSessionId from 'communicator/utils/session-id';
import { localStorage, STARTING_TAB, WELCOME_TABS } from 'communicator/utils/storage';
import { loadThm } from 'communicator/utils/thm';
import { IdentityLookupPayload, IdLookupData } from 'types/visa-sdk/identity-lookup';
import identityLookupLegacy from './identity-lookup-legacy';

export default async function identityLookup(
  event: HandlerEvent<IdLookupData>
): Promise<IdentityLookupPayload | ValidationError> {
  try {
    if (!getIsFeatureToggleEnabled('orchestratorIdLookupApi')) {
      markStart('IDENTITY_LOOKUP_LEGACY');
      return identityLookupLegacy(event);
    }

    const { enableVpps, useSessionId32Char } = getFeatureToggles();

    const stime = Date.now();
    markStart('IDENTITY_LOOKUP');
    const { data } = event;

    pushIdentityLookupRequest();

    const lookupResponse = await fetchOrchestratorIdLookup({
      apikey: getApiKey(),
      identityType: data.consumerIdentity.identityType,
      identityValue:
        data.consumerIdentity.identityType === 'MOBILE_PHONE_NUMBER'
          ? data.consumerIdentity.identityValue.replace(/^\+/, '')
          : data.consumerIdentity.identityValue,
      isVdiDisabled: getIsVdiDisabled(),
      thmProfileHint: getThmProfileHint()
    });

    if (!lookupResponse.ok) {
      const err = getGenericError(lookupResponse);
      markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: err });
      // START GENAI@GHCOPILOT
      pushErrors('IDENTITY_LOOKUP', err);
      // END GENAI@GHCOPILOT
      return error(err);
    }

    // TODO : To be removed once new translation logic kicks in (Reference ticket: )
    if (
      getIsFeatureToggleEnabled('enableLocaleCookie') &&
      lookupResponse.data.customData?.locality.locale
    ) {
      setCookie('locale', lookupResponse.data.customData.locality.locale);
    }

    const lookupStatus =
      lookupResponse.data.consumerStatus === 'ACTIVE'
        ? 'PRESENT'
        : lookupResponse.data.consumerPresent1 === false
          ? 'NOTPRESENT'
          : 'USERNOTALLOWED';

    pushIdentityLookupResponse(lookupStatus, Date.now() - stime);
    const isLocked =
      lookupResponse.data.consumerStatus === 'LOCKED' ||
      lookupResponse.data.consumerStatus === 'SUSPENDED';
    // VCO-15249 - Relaunch THM with new sessionId if account is locked

    if (isLocked) {
      saveMeta({
        sessionId: createSessionId(enableVpps, useSessionId32Char)
      });

      loadThm({
        isVisaSrci: getIsVisaSRCi(),
        orgId: getOrgId(),
        sessionId: getSessionId()
      });

      // localStorage is used to determine whether to show login or CFE form in Visa SRCi flows
      localStorage.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
      markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: 'ACCT_INACCESSIBLE' });

      // START GENAI@GHCOPILOT
      pushErrors('IDENTITY_LOOKUP', 'ACCT_INACCESSIBLE');
      // END GENAI@GHCOPILOT
      return error('ACCT_INACCESSIBLE');
    }

    //START GENAI@GHCOPILOT
    const consumerPresent = lookupStatus === 'PRESENT';
    let lastUsedCardTimestamp;

    if (
      getIsFeatureToggleEnabled('enableLastUsedTs') &&
      lookupResponse.data.customData?.lastUsedCardTimestamp
    ) {
      lastUsedCardTimestamp = lookupResponse.data.customData.lastUsedCardTimestamp;
    }

    const payload: IdentityLookupPayload = { consumerPresent, lastUsedCardTimestamp };
    //END GENAI@GHCOPILOT
    const userUpdates: Partial<UserProfile> = {
      email: getIsFeatureToggleEnabled('enableMobileAsId')
        ? data.consumerIdentity.identityType === 'EMAIL_ADDRESS'
          ? data.consumerIdentity.identityValue
          : ''
        : data.consumerIdentity.identityValue,
      identityType: data.consumerIdentity.identityType,
      isLocked,
      lookupStatus,
      phone:
        data.consumerIdentity.identityType === 'MOBILE_PHONE_NUMBER'
          ? data.consumerIdentity.identityValue.replace(/^\+/, '')
          : ''
    };

    if (consumerPresent) {
      // localStorage is used to determine whether to show login or CFE form in Visa SRCi flows
      localStorage.setItem(STARTING_TAB, WELCOME_TABS.SIGN_IN);
      const newAccessToken = getHeader(lookupResponse, 'access_token') as string;
      const expiresIn = parseExpiryFromAccessToken(newAccessToken) as number;
      userUpdates.accessToken = {
        expiresIn,
        isPartial: true,
        token: newAccessToken
      };

      userUpdates.country = lookupResponse.data.customData?.locality.country;
      userUpdates.locale = lookupResponse.data.customData?.locality.locale;
    }

    saveUser(userUpdates);
    if (getIsVisaSRCi() && lookupResponse.data.customData) {
      payload.customData = {
        data: {
          country: lookupResponse.data.customData.locality.country,
          locale: lookupResponse.data.customData.locality.locale
        }
      };
    }

    markEnd('IDENTITY_LOOKUP');
    return payload;
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('IDENTITY_LOOKUP', { doFlush: true, failure: true, reason: sErr });
    pushErrors('IDENTITY_LOOKUP', sErr);
    return error('IDENTITY_LOOKUP_ERR', sErr);
    // END GENAI@GHCOPILOT
  }
}
